'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Preferred first: MP4 plays everywhere (Chrome 126+ and Safari record it natively);
// WebM is the fallback in Firefox and older Chrome.
const TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

function pickType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function BrowserScreenRecorder({ config }: { config?: { toolSlug?: string } }) {
  const [supported, setSupported] = useState(true);
  const [state, setState] = useState<'idle' | 'recording' | 'paused' | 'done'>('idle');
  const [withMic, setWithMic] = useState(true);
  const [withSystem, setWithSystem] = useState(true);
  const [quality, setQuality] = useState('5000000');
  const [seconds, setSeconds] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState(0);
  const [ext, setExt] = useState('webm');
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const streams = useRef<MediaStream[]>([]);
  const chunks = useRef<Blob[]>([]);
  const audioCtx = useRef<AudioContext | null>(null);
  const live = useRef<HTMLVideoElement>(null);
  const reported = useRef(false);

  useEffect(() => {
    setSupported(
      Boolean(navigator.mediaDevices?.getDisplayMedia) && typeof MediaRecorder !== 'undefined',
    );
  }, []);

  useEffect(() => {
    if (state !== 'recording') return;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [state]);

  useEffect(
    () => () => {
      streams.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      if (url) URL.revokeObjectURL(url);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function cleanup() {
    streams.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streams.current = [];
    void audioCtx.current?.close();
    audioCtx.current = null;
    if (live.current) live.current.srcObject = null;
  }

  async function start() {
    setError(null);
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: withSystem,
      });
      streams.current = [display];
      let mic: MediaStream | null = null;
      if (withMic) {
        try {
          mic = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true },
          });
          streams.current.push(mic);
        } catch {
          setError(
            'Microphone access was blocked, so the recording has no voice. You can allow it in the address bar.',
          );
        }
      }
      // Mix system audio and microphone into one track; MediaRecorder records a single audio track.
      const audioSources = [display, mic].filter((s): s is MediaStream =>
        Boolean(s?.getAudioTracks().length),
      );
      let audioTrack: MediaStreamTrack | null = null;
      if (audioSources.length === 1) audioTrack = audioSources[0].getAudioTracks()[0];
      if (audioSources.length > 1) {
        const ctx = new AudioContext();
        audioCtx.current = ctx;
        const dest = ctx.createMediaStreamDestination();
        audioSources.forEach((s) => ctx.createMediaStreamSource(s).connect(dest));
        audioTrack = dest.stream.getAudioTracks()[0];
      }
      const tracks = [...display.getVideoTracks(), ...(audioTrack ? [audioTrack] : [])];
      const stream = new MediaStream(tracks);
      const type = pickType();
      setExt(type.startsWith('video/mp4') ? 'mp4' : 'webm');
      const rec = new MediaRecorder(stream, {
        ...(type ? { mimeType: type } : {}),
        videoBitsPerSecond: Number(quality),
      });
      chunks.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunks.current, { type: rec.mimeType || type || 'video/webm' });
        setSize(blob.size);
        setUrl(URL.createObjectURL(blob));
        setState('done');
        cleanup();
      };
      // The browser's own "Stop sharing" button ends the video track; finish the file then.
      display.getVideoTracks()[0].addEventListener('ended', () => {
        if (rec.state !== 'inactive') rec.stop();
      });
      rec.start(1000);
      recorder.current = rec;
      if (live.current) live.current.srcObject = display;
      setSeconds(0);
      setState('recording');
      if (!reported.current) {
        reported.current = true;
        void consumeToolUsage(config?.toolSlug ?? 'screen-recorder', '').catch(() => undefined);
      }
    } catch (e) {
      cleanup();
      const name = (e as DOMException)?.name;
      setError(
        name === 'NotAllowedError'
          ? 'Screen sharing was cancelled or blocked. Press Start and choose a screen, window or tab.'
          : 'The recording could not start in this browser.',
      );
      setState('idle');
    }
  }

  const stop = () => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
  };
  const pause = () => {
    const r = recorder.current;
    if (!r) return;
    if (r.state === 'recording') {
      r.pause();
      setState('paused');
    } else if (r.state === 'paused') {
      r.resume();
      setState('recording');
    }
  };

  const stamp = new Date().toISOString().slice(0, 16).replace('T', '-').replace(':', '');

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Recorder</span>
        <h2 className="tool-live-title">Screen recorder</h2>
        <p className="tool-live-subtitle">
          Record your screen, a window or a browser tab with sound. No install, no watermark, no
          time limit; the video never leaves your computer.
        </p>
      </div>
      {!supported ? (
        <p className="tool-live-panel-note">
          Screen recording needs a desktop browser: Chrome, Edge, Firefox or Safari on a computer.
          Phones do not allow web pages to capture the screen; use the built-in screen recorder in
          the control centre (iPhone) or quick settings (Android).
        </p>
      ) : (
        <div className="tool-live-grid">
          <section className="tool-live-uploader">
            {state === 'idle' || state === 'done' ? (
              <>
                <label
                  className="tool-live-field tool-live-field-full"
                  style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}
                >
                  <input
                    checked={withMic}
                    onChange={(e) => setWithMic(e.target.checked)}
                    type="checkbox"
                  />
                  <span>Record my microphone (voice-over)</span>
                </label>
                <label
                  className="tool-live-field tool-live-field-full"
                  style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}
                >
                  <input
                    checked={withSystem}
                    onChange={(e) => setWithSystem(e.target.checked)}
                    type="checkbox"
                  />
                  <span>Record computer or tab sound</span>
                </label>
                <label className="tool-live-field">
                  <span>Quality</span>
                  <select onChange={(e) => setQuality(e.target.value)} value={quality}>
                    <option value="2500000">Standard (smaller file)</option>
                    <option value="5000000">High</option>
                    <option value="10000000">Very high (large file)</option>
                  </select>
                </label>
                <div className="tool-live-actions">
                  <button className="button" onClick={() => void start()} type="button">
                    {state === 'done' ? 'Record again' : 'Start recording'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p
                  className="tool-live-title"
                  style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {state === 'paused' ? '⏸' : '●'} {fmt(seconds)}
                </p>
                <div className="tool-live-actions">
                  <button className="button" onClick={stop} type="button">
                    Stop and save
                  </button>
                  <button className="button button-secondary" onClick={pause} type="button">
                    {state === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                </div>
              </>
            )}
            {error ? <p className="status-message status-error">{error}</p> : null}
          </section>
          <section className="tool-live-preview">
            {state === 'done' && url ? (
              <>
                <video
                  controls
                  src={url}
                  style={{ width: '100%', borderRadius: 8, background: '#000' }}
                />
                <div className="tool-live-actions">
                  <a className="button" download={`screen-recording-${stamp}.${ext}`} href={url}>
                    Download {ext.toUpperCase()} ({(size / 1048576).toFixed(1)} MB)
                  </a>
                </div>
                {ext === 'webm' ? (
                  <p className="tool-live-panel-note">
                    Saved as WebM, which Chrome, Firefox and VLC play. Need MP4 for iPhone or an
                    editor? Use the <Link href="/tools/webm-to-mp4">WebM to MP4 converter</Link>.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <video
                  autoPlay
                  muted
                  playsInline
                  ref={live}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    background: '#000',
                    aspectRatio: '16 / 9',
                    display: state === 'idle' ? 'none' : 'block',
                  }}
                />
                {state === 'idle' ? (
                  <p className="tool-live-panel-note">
                    Press Start, then pick the entire screen, a window or a tab. To capture sound
                    from a tab in Chrome, choose the tab and tick “Share tab audio”. Windows can
                    share whole-system audio; macOS shares tab audio only.
                  </p>
                ) : null}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
